/**
 * Per-section-week recording metadata, written by the zoom-downloader as it
 * uploads each recording file to the `cip-zoom-recordings` bucket, and read by
 * consumers (e.g. section shadowing's `getshadowmaterials`) so they can sign
 * known object paths instead of listing the bucket.
 *
 * Doc: `sections/{sectionId}/recordings/{sectionWeek}` — **1-based** sectionWeek,
 * the FE-facing convention already used by the shadowing assignment/coverage/
 * feedback docs, so readers key everything off the same number with no ±1 (see
 * #1484). The recordings *bucket* still uses a 0-based directory
 * (`sections/{sectionId}/{weekIndex}/…`); the downloader converts, and the
 * stored `path` values point at those real 0-based objects, so the 0-based
 * layout never surfaces as a week number to consumers. Keyed by section + week
 * rather than course because the downloader has no courseId for section
 * recordings; the global `sections` collection already scopes it.
 */
import {z} from 'zod';

import type {SectionId, SectionWeekId} from '../../../section/sectionApi';
import type {RecordingFile} from '../../../zoom';
import {FirestoreTimestampSchema} from '../../types';
import {SECTION_COLLECTION_PATH} from './sectionDocument';

/** Subcollection under a section doc holding per-week recording metadata. */
export const SECTION_RECORDINGS_SUBCOLLECTION = 'recordings' as const;

/**
 * One recording file's stored metadata, keyed in the section-week doc by its
 * Zoom recording file id.
 */
export const SectionRecordingRenderingSchema = z.object({
  /** Zoom `file_type`, e.g. `MP4`, `TRANSCRIPT`, `M4A`. */
  fileType: z.string(),
  /**
   * Zoom `recording_type` for video files (e.g. `shared_screen`,
   * `active_speaker`, `gallery_view`); null for file types without a
   * meaningful one.
   */
  recordingType: z.string().nullable(),
  /** Full object path in the recordings bucket (`sections/{id}/{week}/…`). */
  path: z.string(),
  /** Stored content type (e.g. `video/mp4`); null when unknown. */
  contentType: z.string().nullable(),
  /** ISO 8601 segment start, for ordering/aligning renderings; null if absent. */
  recordingStart: z.string().nullable(),
  /** ISO 8601 segment end; null if absent. */
  recordingEnd: z.string().nullable(),
});

/** One recording file's stored metadata. */
export type SectionRecordingRendering = z.infer<
  typeof SectionRecordingRenderingSchema
>;

/**
 * The section-week recordings doc: a map of renderings keyed by Zoom recording
 * file id, so each file's upload merges its own entry idempotently.
 */
export const SectionRecordingsDocumentSchema = z.object({
  /** Renderings keyed by Zoom recording file id. */
  renderings: z.record(z.string(), SectionRecordingRenderingSchema),
  /** Server time of the last rendering write; absent until the first write. */
  updatedAt: FirestoreTimestampSchema.optional(),
});

/** A stored section-week recordings doc. */
export type SectionRecordingsDocument = z.infer<
  typeof SectionRecordingsDocumentSchema
>;

/**
 * Document path for a section-week's recording metadata:
 * `sections/{sectionId}/recordings/{sectionWeek}`.
 *
 * @param sectionId - The (global) section id.
 * @param sectionWeek - 1-based section week (the FE-facing convention, matching
 *   the shadowing assignment/coverage/feedback docs). NOTE: this is not the
 *   0-based `weekIndex` of the GCS recordings layout — the downloader converts.
 * @return The Firestore document path.
 */
export function getSectionRecordingsDocumentPath({
  sectionId,
  sectionWeek,
}: {
  sectionId: SectionId;
  sectionWeek: SectionWeekId;
}): string {
  return `${SECTION_COLLECTION_PATH}/${sectionId}/${SECTION_RECORDINGS_SUBCOLLECTION}/${String(sectionWeek)}`;
}

/**
 * Resolves the content type to store for a recording file. Zoom's download
 * response mislabels mp4s as `application/octet-stream`, so force `video/mp4`
 * for MP4 files; otherwise use the provided fallback.
 *
 * @param fileType - The Zoom `file_type` (e.g. `MP4`, `TRANSCRIPT`).
 * @param fallbackContentType - The content type from the download response, if
 *   any; returned unchanged for non-mp4 files (may be undefined).
 * @return The content type to store, or undefined when non-mp4 and no fallback.
 */
export function resolveRecordingContentType({
  fileType,
  fallbackContentType,
}: {
  fileType: string;
  fallbackContentType: string | undefined;
}): string | undefined {
  if (fileType === 'MP4') {
    return 'video/mp4';
  }
  return fallbackContentType;
}

/**
 * Builds the stored rendering metadata for one recording file.
 *
 * @param recordingFile - The Zoom recording file.
 * @param path - The object path it was uploaded to in the recordings bucket.
 * @param contentType - The content type stored on the object (see
 *   {@link resolveRecordingContentType}); undefined stores null.
 * @return The rendering entry to merge into the section-week recordings doc.
 */
export function buildSectionRecordingRendering({
  recordingFile,
  path,
  contentType,
}: {
  recordingFile: RecordingFile;
  path: string;
  contentType: string | undefined;
}): SectionRecordingRendering {
  return {
    fileType: recordingFile.file_type,
    recordingType: recordingFile.recording_type || null,
    path,
    contentType: contentType ?? null,
    recordingStart: recordingFile.recording_start ?? null,
    recordingEnd: recordingFile.recording_end ?? null,
  };
}
